import { normalizeDocumentPageSettings } from './textEditorDocumentPageSettings';

/** Extension for reopenable local documents (JSON bundle with page settings + HTML). */
export const TEXT_EDITOR_LOCAL_DOCUMENT_EXTENSION = '.bahlusdoc';

export const TEXT_EDITOR_LOCAL_DOCUMENT_ACCEPT = `${TEXT_EDITOR_LOCAL_DOCUMENT_EXTENSION},application/vnd.bahlus.document+json,application/json`;

const FILE_KIND = 'bahlus-text-editor-document';
const FILE_VERSION = 1;

export function buildLocalDocumentFilePayload({
  html = '',
  documentPageSettings = null,
  title = 'Untitled document',
  mode = 'rich',
}) {
  return {
    version: FILE_VERSION,
    kind: FILE_KIND,
    mode: mode === 'markdown' ? 'markdown' : 'rich',
    title: String(title || 'Untitled document').slice(0, 240),
    savedAt: new Date().toISOString(),
    documentPageSettings: documentPageSettings
      ? normalizeDocumentPageSettings(documentPageSettings)
      : null,
    html: String(html || ''),
  };
}

export function serializeLocalDocumentFile(payload) {
  return `${JSON.stringify(payload, null, 2)}\n`;
}

export function parseLocalDocumentFile(rawText) {
  const parsed = JSON.parse(String(rawText || ''));
  if (!parsed || parsed.kind !== FILE_KIND || parsed.version !== FILE_VERSION) {
    throw new Error('This file is not a valid Bahlus document (.bahlusdoc).');
  }
  return {
    html: String(parsed.html || ''),
    mode: parsed.mode === 'markdown' ? 'markdown' : 'rich',
    title: String(parsed.title || 'Untitled document'),
    documentPageSettings: parsed.documentPageSettings
      ? normalizeDocumentPageSettings(parsed.documentPageSettings)
      : null,
  };
}

export function getLocalDocumentFileName(baseName, payload) {
  const safeBase = String(baseName || payload?.title || 'document')
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\.[^./\\]+$/i, '')
    .trim()
    .slice(0, 120);
  return `${safeBase || 'document'}${TEXT_EDITOR_LOCAL_DOCUMENT_EXTENSION}`;
}

export function buildLocalDocumentBlob(payload) {
  return new Blob([serializeLocalDocumentFile(payload)], {
    type: 'application/vnd.bahlus.document+json;charset=utf-8',
  });
}

const LOCAL_DOCUMENT_SAVE_TYPES = [
  {
    description: 'Bahlus document',
    accept: {
      'application/json': [TEXT_EDITOR_LOCAL_DOCUMENT_EXTENSION],
    },
  },
];

/** True when the browser can show the OS “Save as” / choose-folder dialog. */
export function canUseNativeSaveAsDialog() {
  return typeof window.showSaveFilePicker === 'function' && window.isSecureContext;
}

function buildSaveFilePickerOptions(fileName, { withTypes = true } = {}) {
  const options = {
    suggestedName: fileName,
    excludeAcceptAllOption: false,
    startIn: 'documents',
  };
  if (withTypes) {
    options.types = LOCAL_DOCUMENT_SAVE_TYPES;
  }
  return options;
}

/** Call synchronously from a click handler (preserves user activation). */
function startNativeSaveFilePicker(fileName, withTypes) {
  return window.showSaveFilePicker(buildSaveFilePickerOptions(fileName, { withTypes }));
}

function writeBlobViaDownloadAnchor(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

async function writeBlobToFileHandle(handle, blob) {
  const writable = await handle.createWritable();
  await writable.write(blob);
  await writable.close();
}

function fallbackDownload(blob, fileName) {
  writeBlobViaDownloadAnchor(blob, fileName);
  if (!canUseNativeSaveAsDialog()) {
    window.alert(
      'This browser cannot open a Save As folder picker here. The file was downloaded instead. For a choose-where-to-save dialog, use Chrome or Edge on this site, or turn on “Ask where to save each file” in your browser settings.'
    );
  }
}

/** Programmatic download (browser may save to Downloads without asking). */
export function downloadLocalDocumentFile(payload, baseName) {
  const fileName = getLocalDocumentFileName(baseName, payload);
  const blob = buildLocalDocumentBlob(payload);
  writeBlobViaDownloadAnchor(blob, fileName);
}

/**
 * Opens the OS Save As dialog first (choose folder + filename), then writes the document.
 * `getPayload` runs after the user picks a location so the picker stays tied to the menu click.
 */
export function promptSaveLocalDocumentPickerFirst(getPayload, baseName) {
  const fileName = getLocalDocumentFileName(baseName, { title: baseName });

  if (!canUseNativeSaveAsDialog()) {
    const payload = getPayload();
    fallbackDownload(buildLocalDocumentBlob(payload), fileName);
    return Promise.resolve(true);
  }

  let pickerPromise;
  try {
    pickerPromise = startNativeSaveFilePicker(fileName, true);
  } catch (err) {
    if (err?.name === 'AbortError') return Promise.resolve(false);
    try {
      pickerPromise = startNativeSaveFilePicker(fileName, false);
    } catch (err2) {
      if (err2?.name === 'AbortError') return Promise.resolve(false);
      const payload = getPayload();
      fallbackDownload(buildLocalDocumentBlob(payload), fileName);
      return Promise.resolve(true);
    }
  }

  return pickerPromise
    .catch((err) => {
      if (err?.name === 'AbortError') throw err;
      return startNativeSaveFilePicker(fileName, false);
    })
    .then((handle) => {
      const payload = getPayload();
      return writeBlobToFileHandle(handle, buildLocalDocumentBlob(payload));
    })
    .then(() => true)
    .catch((err) => {
      if (err?.name === 'AbortError') return false;
      const payload = getPayload();
      fallbackDownload(buildLocalDocumentBlob(payload), fileName);
      return true;
    });
}

/**
 * Save with an already-built payload. Prefer {@link promptSaveLocalDocumentPickerFirst} from File menu clicks.
 */
export function promptSaveLocalDocument(payload, baseName) {
  return promptSaveLocalDocumentPickerFirst(() => payload, baseName);
}

/** @deprecated Use promptSaveLocalDocumentPickerFirst from click handlers */
export async function saveLocalDocumentToDevice(payload, baseName) {
  return promptSaveLocalDocument(payload, baseName);
}
